import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { 
  Building2, Users, Briefcase, Landmark, Plus, Edit, Trash2, Save, 
  Loader2, Clock, Settings, ChevronRight, Percent, Yen, AlertCircle, UserCog
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const MasterSettingsPage = () => {
  const [activeTab, setActiveTab] = useState("patterns");
  const [loading, setLoading] = useState(true);
  
  // 勤怠パターン
  const [patterns, setPatterns] = useState([]);
  const [showPatternDialog, setShowPatternDialog] = useState(false);
  const [editingPattern, setEditingPattern] = useState(null);
  const [savingPattern, setSavingPattern] = useState(false);
  const [patternForm, setPatternForm] = useState({
    code: "",
    name: "",
    work_start_time: "09:00",
    work_end_time: "18:00",
    break_start_time: "12:00",
    break_end_time: "13:00",
    break_minutes: 60,
    scheduled_work_minutes: 480,
    night_start_time: "22:00",
    night_end_time: "05:00",
    is_flex: false,
    round_unit: 15,
    round_type_in: "down",
    round_type_out: "down",
    color: "#3b82f6",
    is_default: false
  });
  
  // 時間外割増率
  const [overtimeRates, setOvertimeRates] = useState({
    normal_overtime: 1.25,
    late_night: 1.50,
    holiday_work: 1.35,
    holiday_overtime: 1.35,
    holiday_late_night: 1.60,
    over_60h_addition: 0.25,
    absence_deduction: -1.00,
    late_deduction: -1.00,
    early_leave_deduction: -1.00,
    calculation_method: "rate"
  });
  const [savingRates, setSavingRates] = useState(false);
  
  // 会社勤怠設定
  const [companySettings, setCompanySettings] = useState({
    closing_day: 25,
    pay_day: 25,
    pay_month_offset: 1,
    paid_leave_grant_month: 4,
    paid_leave_grant_day: 1,
    monthly_overtime_limit: 45,
    yearly_overtime_limit: 360,
    special_overtime_limit: 100,
    timecard_format: "standard",
    show_overtime_breakdown: true
  });
  const [savingCompanySettings, setSavingCompanySettings] = useState(false);
  
  // 部門マスタ
  const [departments, setDepartments] = useState([]);
  const [showDepartmentDialog, setShowDepartmentDialog] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [savingDepartment, setSavingDepartment] = useState(false);
  const [departmentForm, setDepartmentForm] = useState({
    code: "",
    name: "",
    name_kana: "",
    sort_order: 0
  });
  
  // 役職マスタ
  const [positions, setPositions] = useState([]);
  const [showPositionDialog, setShowPositionDialog] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [savingPosition, setSavingPosition] = useState(false);
  const [positionForm, setPositionForm] = useState({
    code: "",
    name: "",
    name_kana: "",
    rank: 0,
    allowance: 0,
    sort_order: 0
  });
  
  // 雇用形態マスタ
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [showEmploymentTypeDialog, setShowEmploymentTypeDialog] = useState(false);
  const [editingEmploymentType, setEditingEmploymentType] = useState(null);
  const [savingEmploymentType, setSavingEmploymentType] = useState(false);
  const [employmentTypeForm, setEmploymentTypeForm] = useState({
    code: "",
    name: "",
    description: "",
    sort_order: 0
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchPatterns(),
        fetchOvertimeRates(),
        fetchCompanySettings(),
        fetchDepartments(),
        fetchPositions(),
        fetchEmploymentTypes()
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // 勤怠パターン関連
  const fetchPatterns = async () => {
    try {
      const res = await axios.get(`${API}/attendance-settings/patterns`);
      setPatterns(res.data);
    } catch (error) {
      console.error("Error fetching patterns:", error);
    }
  };

  const openPatternDialog = (pattern = null) => {
    if (pattern) {
      setEditingPattern(pattern);
      setPatternForm({
        code: pattern.code || "",
        name: pattern.name || "",
        work_start_time: pattern.work_start_time || "09:00",
        work_end_time: pattern.work_end_time || "18:00",
        break_start_time: pattern.break_start_time || "12:00",
        break_end_time: pattern.break_end_time || "13:00",
        break_minutes: pattern.break_minutes || 60,
        scheduled_work_minutes: pattern.scheduled_work_minutes || 480,
        night_start_time: pattern.night_start_time || "22:00",
        night_end_time: pattern.night_end_time || "05:00",
        is_flex: pattern.is_flex || false,
        round_unit: pattern.round_unit || 15,
        round_type_in: pattern.round_type_in || "down",
        round_type_out: pattern.round_type_out || "down",
        color: pattern.color || "#3b82f6",
        is_default: pattern.is_default || false
      });
    } else {
      setEditingPattern(null);
      setPatternForm({
        code: "",
        name: "",
        work_start_time: "09:00",
        work_end_time: "18:00",
        break_start_time: "12:00",
        break_end_time: "13:00",
        break_minutes: 60,
        scheduled_work_minutes: 480,
        night_start_time: "22:00",
        night_end_time: "05:00",
        is_flex: false,
        round_unit: 15,
        round_type_in: "down",
        round_type_out: "down",
        color: "#3b82f6",
        is_default: false
      });
    }
    setShowPatternDialog(true);
  };

  const savePattern = async () => {
    if (!patternForm.code || !patternForm.name) {
      toast.error("コードと名称は必須です");
      return;
    }
    setSavingPattern(true);
    try {
      if (editingPattern) {
        await axios.put(`${API}/attendance-settings/patterns/${editingPattern.id}`, patternForm);
        toast.success("勤怠パターンを更新しました");
      } else {
        await axios.post(`${API}/attendance-settings/patterns`, patternForm);
        toast.success("勤怠パターンを作成しました");
      }
      setShowPatternDialog(false);
      fetchPatterns();
    } catch (error) {
      toast.error(error.response?.data?.detail || "保存に失敗しました");
    } finally {
      setSavingPattern(false);
    }
  };

  const deletePattern = async (pattern) => {
    if (!confirm(`「${pattern.name}」を削除しますか？`)) return;
    try {
      await axios.delete(`${API}/attendance-settings/patterns/${pattern.id}`);
      toast.success("勤怠パターンを削除しました");
      fetchPatterns();
    } catch (error) {
      toast.error(error.response?.data?.detail || "削除に失敗しました");
    }
  };

  // 時間外割増率関連
  const fetchOvertimeRates = async () => {
    try {
      const res = await axios.get(`${API}/attendance-settings/overtime-rates`);
      setOvertimeRates(res.data);
    } catch (error) {
      console.error("Error fetching overtime rates:", error);
    }
  };

  const saveOvertimeRates = async () => {
    setSavingRates(true);
    try {
      await axios.post(`${API}/attendance-settings/overtime-rates`, overtimeRates);
      toast.success("時間外割増率を保存しました");
    } catch (error) {
      toast.error("保存に失敗しました");
    } finally {
      setSavingRates(false);
    }
  };

  // 会社勤怠設定関連
  const fetchCompanySettings = async () => {
    try {
      const res = await axios.get(`${API}/attendance-settings/company`);
      setCompanySettings(res.data);
    } catch (error) {
      console.error("Error fetching company settings:", error);
    }
  };

  const saveCompanySettings = async () => {
    setSavingCompanySettings(true);
    try {
      await axios.post(`${API}/attendance-settings/company`, companySettings);
      toast.success("勤怠設定を保存しました");
    } catch (error) {
      toast.error("保存に失敗しました");
    } finally {
      setSavingCompanySettings(false);
    }
  };

  // 部門マスタ関連
  const fetchDepartments = async () => {
    try {
      const res = await axios.get(`${API}/masters/departments`);
      setDepartments(res.data);
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  const openDepartmentDialog = (department = null) => {
    if (department) {
      setEditingDepartment(department);
      setDepartmentForm({
        code: department.code || "",
        name: department.name || "",
        name_kana: department.name_kana || "",
        sort_order: department.sort_order || 0
      });
    } else {
      setEditingDepartment(null);
      setDepartmentForm({ code: "", name: "", name_kana: "", sort_order: 0 });
    }
    setShowDepartmentDialog(true);
  };

  const saveDepartment = async () => {
    if (!departmentForm.code || !departmentForm.name) {
      toast.error("コードと名称は必須です");
      return;
    }
    setSavingDepartment(true);
    try {
      if (editingDepartment) {
        await axios.put(`${API}/masters/departments/${editingDepartment.id}`, departmentForm);
        toast.success("部門を更新しました");
      } else {
        await axios.post(`${API}/masters/departments`, departmentForm);
        toast.success("部門を作成しました");
      }
      setShowDepartmentDialog(false);
      fetchDepartments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "保存に失敗しました");
    } finally {
      setSavingDepartment(false);
    }
  };

  const deleteDepartment = async (department) => {
    if (!confirm(`「${department.name}」を削除しますか？`)) return;
    try {
      await axios.delete(`${API}/masters/departments/${department.id}`);
      toast.success("部門を削除しました");
      fetchDepartments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "削除に失敗しました");
    }
  };

  // 役職マスタ関連
  const fetchPositions = async () => {
    try {
      const res = await axios.get(`${API}/masters/positions`);
      setPositions(res.data);
    } catch (error) {
      console.error("Error fetching positions:", error);
    }
  };

  const openPositionDialog = (position = null) => {
    if (position) {
      setEditingPosition(position);
      setPositionForm({
        code: position.code || "",
        name: position.name || "",
        name_kana: position.name_kana || "",
        rank: position.rank || 0,
        allowance: position.allowance || 0,
        sort_order: position.sort_order || 0
      });
    } else {
      setEditingPosition(null);
      setPositionForm({ code: "", name: "", name_kana: "", rank: 0, allowance: 0, sort_order: 0 });
    }
    setShowPositionDialog(true);
  };

  const savePosition = async () => {
    if (!positionForm.code || !positionForm.name) {
      toast.error("コードと名称は必須です");
      return;
    }
    setSavingPosition(true);
    try {
      if (editingPosition) {
        await axios.put(`${API}/masters/positions/${editingPosition.id}`, positionForm);
        toast.success("役職を更新しました");
      } else {
        await axios.post(`${API}/masters/positions`, positionForm);
        toast.success("役職を作成しました");
      }
      setShowPositionDialog(false);
      fetchPositions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "保存に失敗しました");
    } finally {
      setSavingPosition(false);
    }
  };

  const deletePosition = async (position) => {
    if (!confirm(`「${position.name}」を削除しますか？`)) return;
    try {
      await axios.delete(`${API}/masters/positions/${position.id}`);
      toast.success("役職を削除しました");
      fetchPositions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "削除に失敗しました");
    }
  };

  // 雇用形態マスタ関連
  const fetchEmploymentTypes = async () => {
    try {
      const res = await axios.get(`${API}/masters/employment-types`);
      setEmploymentTypes(res.data);
    } catch (error) {
      console.error("Error fetching employment types:", error);
    }
  };

  const openEmploymentTypeDialog = (employmentType = null) => {
    if (employmentType) {
      setEditingEmploymentType(employmentType);
      setEmploymentTypeForm({
        code: employmentType.code || "",
        name: employmentType.name || "",
        description: employmentType.description || "",
        sort_order: employmentType.sort_order || 0
      });
    } else {
      setEditingEmploymentType(null);
      setEmploymentTypeForm({ code: "", name: "", description: "", sort_order: 0 });
    }
    setShowEmploymentTypeDialog(true);
  };

  const saveEmploymentType = async () => {
    if (!employmentTypeForm.code || !employmentTypeForm.name) {
      toast.error("コードと名称は必須です");
      return;
    }
    setSavingEmploymentType(true);
    try {
      if (editingEmploymentType) {
        await axios.put(`${API}/masters/employment-types/${editingEmploymentType.id}`, employmentTypeForm);
        toast.success("雇用形態を更新しました");
      } else {
        await axios.post(`${API}/masters/employment-types`, employmentTypeForm);
        toast.success("雇用形態を作成しました");
      }
      setShowEmploymentTypeDialog(false);
      fetchEmploymentTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "保存に失敗しました");
    } finally {
      setSavingEmploymentType(false);
    }
  };

  const deleteEmploymentType = async (employmentType) => {
    if (!confirm(`「${employmentType.name}」を削除しますか？`)) return;
    try {
      await axios.delete(`${API}/masters/employment-types/${employmentType.id}`);
      toast.success("雇用形態を削除しました");
      fetchEmploymentTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "削除に失敗しました");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Settings className="w-7 h-7 text-emerald-500" />
            マスタ設定
          </h1>
          <p className="text-slate-400 text-sm mt-1">勤怠パターン、割増率、部門・役職マスタの管理</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-800 border border-slate-700 flex flex-wrap h-auto p-1">
          <TabsTrigger value="patterns" className="data-[state=active]:bg-emerald-600 gap-2">
            <Clock className="w-4 h-4" />
            勤怠パターン
          </TabsTrigger>
          <TabsTrigger value="overtime" className="data-[state=active]:bg-emerald-600 gap-2">
            <Percent className="w-4 h-4" />
            時間外割増率
          </TabsTrigger>
          <TabsTrigger value="company" className="data-[state=active]:bg-emerald-600 gap-2">
            <Building2 className="w-4 h-4" />
            勤怠設定
          </TabsTrigger>
          <TabsTrigger value="departments" className="data-[state=active]:bg-emerald-600 gap-2">
            <Building2 className="w-4 h-4" />
            部門
          </TabsTrigger>
          <TabsTrigger value="positions" className="data-[state=active]:bg-emerald-600 gap-2">
            <Briefcase className="w-4 h-4" />
            役職
          </TabsTrigger>
          <TabsTrigger value="employment-types" className="data-[state=active]:bg-emerald-600 gap-2">
            <UserCog className="w-4 h-4" />
            雇用形態
          </TabsTrigger>
        </TabsList>

        {/* 勤怠パターン */}
        <TabsContent value="patterns" className="space-y-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white text-lg">勤怠パターン設定</CardTitle>
                <CardDescription>出勤・退勤時間、休憩時間などの勤務パターンを管理します</CardDescription>
              </div>
              <Button onClick={() => openPatternDialog()} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                新規作成
              </Button>
            </CardHeader>
            <CardContent>
              {patterns.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>勤怠パターンが登録されていません</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {patterns.map(pattern => (
                    <div 
                      key={pattern.id}
                      className="flex items-center justify-between p-4 bg-slate-900 rounded-lg border border-slate-700"
                    >
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-3 h-10 rounded"
                          style={{ backgroundColor: pattern.color }}
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-white">{pattern.name}</span>
                            <Badge variant="outline" className="text-xs">{pattern.code}</Badge>
                            {pattern.is_default && (
                              <Badge className="bg-emerald-600 text-xs">デフォルト</Badge>
                            )}
                          </div>
                          <div className="text-sm text-slate-400 mt-1">
                            {pattern.work_start_time} 〜 {pattern.work_end_time}
                            <span className="mx-2">|</span>
                            休憩: {pattern.break_minutes}分
                            <span className="mx-2">|</span>
                            所定: {Math.floor(pattern.scheduled_work_minutes / 60)}時間{pattern.scheduled_work_minutes % 60}分
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => openPatternDialog(pattern)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-red-400 hover:text-red-300"
                          onClick={() => deletePattern(pattern)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 時間外割増率 */}
        <TabsContent value="overtime" className="space-y-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-lg">時間外割増率設定</CardTitle>
              <CardDescription>残業、深夜、休日出勤などの割増率を設定します</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>普通残業 (×)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={overtimeRates.normal_overtime}
                    onChange={(e) => setOvertimeRates({...overtimeRates, normal_overtime: parseFloat(e.target.value)})}
                    className="bg-slate-900 border-slate-700"
                  />
                  <p className="text-xs text-slate-500">法定: 1.25以上</p>
                </div>
                <div className="space-y-2">
                  <Label>深夜残業 (×)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={overtimeRates.late_night}
                    onChange={(e) => setOvertimeRates({...overtimeRates, late_night: parseFloat(e.target.value)})}
                    className="bg-slate-900 border-slate-700"
                  />
                  <p className="text-xs text-slate-500">22:00〜05:00 法定: 1.50以上</p>
                </div>
                <div className="space-y-2">
                  <Label>休日出勤 (×)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={overtimeRates.holiday_work}
                    onChange={(e) => setOvertimeRates({...overtimeRates, holiday_work: parseFloat(e.target.value)})}
                    className="bg-slate-900 border-slate-700"
                  />
                  <p className="text-xs text-slate-500">法定: 1.35以上</p>
                </div>
                <div className="space-y-2">
                  <Label>休日残業 (×)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={overtimeRates.holiday_overtime}
                    onChange={(e) => setOvertimeRates({...overtimeRates, holiday_overtime: parseFloat(e.target.value)})}
                    className="bg-slate-900 border-slate-700"
                  />
                </div>
                <div className="space-y-2">
                  <Label>休日深夜残業 (×)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={overtimeRates.holiday_late_night}
                    onChange={(e) => setOvertimeRates({...overtimeRates, holiday_late_night: parseFloat(e.target.value)})}
                    className="bg-slate-900 border-slate-700"
                  />
                  <p className="text-xs text-slate-500">法定: 1.60以上</p>
                </div>
                <div className="space-y-2">
                  <Label>60時間超加算 (×)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={overtimeRates.over_60h_addition}
                    onChange={(e) => setOvertimeRates({...overtimeRates, over_60h_addition: parseFloat(e.target.value)})}
                    className="bg-slate-900 border-slate-700"
                  />
                  <p className="text-xs text-slate-500">月60時間超の時間外労働</p>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-700">
                <h4 className="font-medium text-white mb-4">勤怠減額設定</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>欠勤控除率</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={overtimeRates.absence_deduction}
                      onChange={(e) => setOvertimeRates({...overtimeRates, absence_deduction: parseFloat(e.target.value)})}
                      className="bg-slate-900 border-slate-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>遅刻控除率</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={overtimeRates.late_deduction}
                      onChange={(e) => setOvertimeRates({...overtimeRates, late_deduction: parseFloat(e.target.value)})}
                      className="bg-slate-900 border-slate-700"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>早退控除率</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={overtimeRates.early_leave_deduction}
                      onChange={(e) => setOvertimeRates({...overtimeRates, early_leave_deduction: parseFloat(e.target.value)})}
                      className="bg-slate-900 border-slate-700"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  onClick={saveOvertimeRates}
                  disabled={savingRates}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {savingRates ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  保存
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 会社勤怠設定 */}
        <TabsContent value="company" className="space-y-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white text-lg">会社勤怠設定</CardTitle>
              <CardDescription>締め日、支給日、36協定設定などを管理します</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>締め日</Label>
                  <Select
                    value={companySettings.closing_day.toString()}
                    onValueChange={(v) => setCompanySettings({...companySettings, closing_day: parseInt(v)})}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(31)].map((_, i) => (
                        <SelectItem key={i+1} value={(i+1).toString()}>
                          {i+1}日 {i === 30 && "(末日)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>支給日</Label>
                  <Select
                    value={companySettings.pay_day.toString()}
                    onValueChange={(v) => setCompanySettings({...companySettings, pay_day: parseInt(v)})}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(31)].map((_, i) => (
                        <SelectItem key={i+1} value={(i+1).toString()}>
                          {i+1}日
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>支給月</Label>
                  <Select
                    value={companySettings.pay_month_offset.toString()}
                    onValueChange={(v) => setCompanySettings({...companySettings, pay_month_offset: parseInt(v)})}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">当月</SelectItem>
                      <SelectItem value="1">翌月</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-700">
                <h4 className="font-medium text-white mb-4 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  36協定設定
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>月間残業上限（時間）</Label>
                    <Input
                      type="number"
                      value={companySettings.monthly_overtime_limit}
                      onChange={(e) => setCompanySettings({...companySettings, monthly_overtime_limit: parseInt(e.target.value)})}
                      className="bg-slate-900 border-slate-700"
                    />
                    <p className="text-xs text-slate-500">一般: 45時間</p>
                  </div>
                  <div className="space-y-2">
                    <Label>年間残業上限（時間）</Label>
                    <Input
                      type="number"
                      value={companySettings.yearly_overtime_limit}
                      onChange={(e) => setCompanySettings({...companySettings, yearly_overtime_limit: parseInt(e.target.value)})}
                      className="bg-slate-900 border-slate-700"
                    />
                    <p className="text-xs text-slate-500">一般: 360時間</p>
                  </div>
                  <div className="space-y-2">
                    <Label>特別条項時月間上限（時間）</Label>
                    <Input
                      type="number"
                      value={companySettings.special_overtime_limit}
                      onChange={(e) => setCompanySettings({...companySettings, special_overtime_limit: parseInt(e.target.value)})}
                      className="bg-slate-900 border-slate-700"
                    />
                    <p className="text-xs text-slate-500">上限: 100時間未満</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-700">
                <h4 className="font-medium text-white mb-4">有給休暇付与設定</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>付与月</Label>
                    <Select
                      value={companySettings.paid_leave_grant_month.toString()}
                      onValueChange={(v) => setCompanySettings({...companySettings, paid_leave_grant_month: parseInt(v)})}
                    >
                      <SelectTrigger className="bg-slate-900 border-slate-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[...Array(12)].map((_, i) => (
                          <SelectItem key={i+1} value={(i+1).toString()}>
                            {i+1}月
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>付与日</Label>
                    <Select
                      value={companySettings.paid_leave_grant_day.toString()}
                      onValueChange={(v) => setCompanySettings({...companySettings, paid_leave_grant_day: parseInt(v)})}
                    >
                      <SelectTrigger className="bg-slate-900 border-slate-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[...Array(28)].map((_, i) => (
                          <SelectItem key={i+1} value={(i+1).toString()}>
                            {i+1}日
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  onClick={saveCompanySettings}
                  disabled={savingCompanySettings}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {savingCompanySettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  保存
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 部門マスタ */}
        <TabsContent value="departments" className="space-y-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white text-lg">部門マスタ</CardTitle>
                <CardDescription>組織の部門を管理します</CardDescription>
              </div>
              <Button onClick={() => openDepartmentDialog()} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                新規作成
              </Button>
            </CardHeader>
            <CardContent>
              {departments.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>部門が登録されていません</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {departments.map(dept => (
                    <div 
                      key={dept.id}
                      className="flex items-center justify-between p-3 bg-slate-900 rounded-lg border border-slate-700"
                    >
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">{dept.code}</Badge>
                        <span className="text-white">{dept.name}</span>
                        {dept.name_kana && (
                          <span className="text-sm text-slate-400">({dept.name_kana})</span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => openDepartmentDialog(dept)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-red-400 hover:text-red-300"
                          onClick={() => deleteDepartment(dept)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 役職マスタ */}
        <TabsContent value="positions" className="space-y-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white text-lg">役職マスタ</CardTitle>
                <CardDescription>役職と役職手当を管理します</CardDescription>
              </div>
              <Button onClick={() => openPositionDialog()} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                新規作成
              </Button>
            </CardHeader>
            <CardContent>
              {positions.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>役職が登録されていません</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {positions.map(pos => (
                    <div 
                      key={pos.id}
                      className="flex items-center justify-between p-3 bg-slate-900 rounded-lg border border-slate-700"
                    >
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">{pos.code}</Badge>
                        <span className="text-white">{pos.name}</span>
                        {pos.allowance > 0 && (
                          <Badge className="bg-amber-600">
                            手当: ¥{pos.allowance.toLocaleString()}
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => openPositionDialog(pos)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-red-400 hover:text-red-300"
                          onClick={() => deletePosition(pos)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 雇用形態マスタ */}
        <TabsContent value="employment-types" className="space-y-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white text-lg">雇用形態マスタ</CardTitle>
                <CardDescription>従業員の雇用形態を管理します</CardDescription>
              </div>
              <Button onClick={() => openEmploymentTypeDialog()} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                新規作成
              </Button>
            </CardHeader>
            <CardContent>
              {employmentTypes.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <UserCog className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>雇用形態が登録されていません</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {employmentTypes.map(et => (
                    <div 
                      key={et.id || et.code}
                      className="flex items-center justify-between p-3 bg-slate-900 rounded-lg border border-slate-700"
                    >
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">{et.code}</Badge>
                        <span className="text-white">{et.name}</span>
                        {et.description && (
                          <span className="text-sm text-slate-400">({et.description})</span>
                        )}
                      </div>
                      {!et.is_default ? (
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={() => openEmploymentTypeDialog(et)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            className="text-red-400 hover:text-red-300"
                            onClick={() => deleteEmploymentType(et)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <Badge className="bg-slate-600 text-xs">デフォルト</Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* 勤怠パターンダイアログ */}
      <Dialog open={showPatternDialog} onOpenChange={setShowPatternDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPattern ? "勤怠パターン編集" : "勤怠パターン作成"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>パターンコード *</Label>
                <Input
                  value={patternForm.code}
                  onChange={(e) => setPatternForm({...patternForm, code: e.target.value.toUpperCase()})}
                  placeholder="A"
                  maxLength={5}
                  className="bg-slate-900 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>パターン名 *</Label>
                <Input
                  value={patternForm.name}
                  onChange={(e) => setPatternForm({...patternForm, name: e.target.value})}
                  placeholder="通常勤務"
                  className="bg-slate-900 border-slate-700"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>始業時刻</Label>
                <Input
                  type="time"
                  value={patternForm.work_start_time}
                  onChange={(e) => setPatternForm({...patternForm, work_start_time: e.target.value})}
                  className="bg-slate-900 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>終業時刻</Label>
                <Input
                  type="time"
                  value={patternForm.work_end_time}
                  onChange={(e) => setPatternForm({...patternForm, work_end_time: e.target.value})}
                  className="bg-slate-900 border-slate-700"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>休憩開始</Label>
                <Input
                  type="time"
                  value={patternForm.break_start_time}
                  onChange={(e) => setPatternForm({...patternForm, break_start_time: e.target.value})}
                  className="bg-slate-900 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>休憩終了</Label>
                <Input
                  type="time"
                  value={patternForm.break_end_time}
                  onChange={(e) => setPatternForm({...patternForm, break_end_time: e.target.value})}
                  className="bg-slate-900 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>休憩時間（分）</Label>
                <Input
                  type="number"
                  value={patternForm.break_minutes}
                  onChange={(e) => setPatternForm({...patternForm, break_minutes: parseInt(e.target.value)})}
                  className="bg-slate-900 border-slate-700"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>所定労働時間（分）</Label>
                <Input
                  type="number"
                  value={patternForm.scheduled_work_minutes}
                  onChange={(e) => setPatternForm({...patternForm, scheduled_work_minutes: parseInt(e.target.value)})}
                  className="bg-slate-900 border-slate-700"
                />
                <p className="text-xs text-slate-500">{Math.floor(patternForm.scheduled_work_minutes / 60)}時間{patternForm.scheduled_work_minutes % 60}分</p>
              </div>
              <div className="space-y-2">
                <Label>丸め単位（分）</Label>
                <Select
                  value={patternForm.round_unit.toString()}
                  onValueChange={(v) => setPatternForm({...patternForm, round_unit: parseInt(v)})}
                >
                  <SelectTrigger className="bg-slate-900 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1分</SelectItem>
                    <SelectItem value="5">5分</SelectItem>
                    <SelectItem value="10">10分</SelectItem>
                    <SelectItem value="15">15分</SelectItem>
                    <SelectItem value="30">30分</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>表示色</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={patternForm.color}
                    onChange={(e) => setPatternForm({...patternForm, color: e.target.value})}
                    className="w-12 h-10 p-1 bg-slate-900 border-slate-700"
                  />
                  <Input
                    value={patternForm.color}
                    onChange={(e) => setPatternForm({...patternForm, color: e.target.value})}
                    className="bg-slate-900 border-slate-700"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch
                  checked={patternForm.is_default}
                  onCheckedChange={(checked) => setPatternForm({...patternForm, is_default: checked})}
                />
                <Label>デフォルトパターンに設定</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPatternDialog(false)}>
              キャンセル
            </Button>
            <Button 
              onClick={savePattern}
              disabled={savingPattern}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {savingPattern ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 部門ダイアログ */}
      <Dialog open={showDepartmentDialog} onOpenChange={setShowDepartmentDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>{editingDepartment ? "部門編集" : "部門作成"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>部門コード *</Label>
                <Input
                  value={departmentForm.code}
                  onChange={(e) => setDepartmentForm({...departmentForm, code: e.target.value})}
                  placeholder="001"
                  className="bg-slate-900 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>部門名 *</Label>
                <Input
                  value={departmentForm.name}
                  onChange={(e) => setDepartmentForm({...departmentForm, name: e.target.value})}
                  placeholder="営業部"
                  className="bg-slate-900 border-slate-700"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>部門名カナ</Label>
                <Input
                  value={departmentForm.name_kana}
                  onChange={(e) => setDepartmentForm({...departmentForm, name_kana: e.target.value})}
                  placeholder="エイギョウブ"
                  className="bg-slate-900 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>表示順</Label>
                <Input
                  type="number"
                  value={departmentForm.sort_order}
                  onChange={(e) => setDepartmentForm({...departmentForm, sort_order: parseInt(e.target.value)})}
                  className="bg-slate-900 border-slate-700"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDepartmentDialog(false)}>
              キャンセル
            </Button>
            <Button 
              onClick={saveDepartment}
              disabled={savingDepartment}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {savingDepartment ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 役職ダイアログ */}
      <Dialog open={showPositionDialog} onOpenChange={setShowPositionDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>{editingPosition ? "役職編集" : "役職作成"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>役職コード *</Label>
                <Input
                  value={positionForm.code}
                  onChange={(e) => setPositionForm({...positionForm, code: e.target.value})}
                  placeholder="001"
                  className="bg-slate-900 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>役職名 *</Label>
                <Input
                  value={positionForm.name}
                  onChange={(e) => setPositionForm({...positionForm, name: e.target.value})}
                  placeholder="部長"
                  className="bg-slate-900 border-slate-700"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>役職名カナ</Label>
                <Input
                  value={positionForm.name_kana}
                  onChange={(e) => setPositionForm({...positionForm, name_kana: e.target.value})}
                  placeholder="ブチョウ"
                  className="bg-slate-900 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>役職手当（円）</Label>
                <Input
                  type="number"
                  value={positionForm.allowance}
                  onChange={(e) => setPositionForm({...positionForm, allowance: parseInt(e.target.value)})}
                  className="bg-slate-900 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>表示順</Label>
                <Input
                  type="number"
                  value={positionForm.sort_order}
                  onChange={(e) => setPositionForm({...positionForm, sort_order: parseInt(e.target.value)})}
                  className="bg-slate-900 border-slate-700"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPositionDialog(false)}>
              キャンセル
            </Button>
            <Button 
              onClick={savePosition}
              disabled={savingPosition}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {savingPosition ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 雇用形態ダイアログ */}
      <Dialog open={showEmploymentTypeDialog} onOpenChange={setShowEmploymentTypeDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>{editingEmploymentType ? "雇用形態編集" : "雇用形態作成"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>雇用形態コード *</Label>
                <Input
                  value={employmentTypeForm.code}
                  onChange={(e) => setEmploymentTypeForm({...employmentTypeForm, code: e.target.value})}
                  placeholder="regular"
                  className="bg-slate-900 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>雇用形態名 *</Label>
                <Input
                  value={employmentTypeForm.name}
                  onChange={(e) => setEmploymentTypeForm({...employmentTypeForm, name: e.target.value})}
                  placeholder="正社員"
                  className="bg-slate-900 border-slate-700"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>説明</Label>
                <Input
                  value={employmentTypeForm.description}
                  onChange={(e) => setEmploymentTypeForm({...employmentTypeForm, description: e.target.value})}
                  placeholder="フルタイム勤務"
                  className="bg-slate-900 border-slate-700"
                />
              </div>
              <div className="space-y-2">
                <Label>表示順</Label>
                <Input
                  type="number"
                  value={employmentTypeForm.sort_order}
                  onChange={(e) => setEmploymentTypeForm({...employmentTypeForm, sort_order: parseInt(e.target.value)})}
                  className="bg-slate-900 border-slate-700"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmploymentTypeDialog(false)}>
              キャンセル
            </Button>
            <Button 
              onClick={saveEmploymentType}
              disabled={savingEmploymentType}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {savingEmploymentType ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MasterSettingsPage;
